import { useEffect, useId, useRef, useState } from "react";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { ButtonBase, Link, Popover, Stack, useTheme } from "@mui/material";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { Link as RouterLink } from "react-router-dom";

import type { RuleListUsage } from "./ruleListRows";

interface UsedInProps {
  usages: readonly RuleListUsage[] | null;
  /**
   * Colour of the experience names. The rules list reads them as secondary
   * text; the rule-detail byline, where they are the sentence's subject, as
   * primary.
   */
  namesColor?: "text.secondary" | "text.primary";
  /**
   * Body 1 (14px/21px) in the rules table; Body 2 (12px/18px) in the rule
   * header's byline.
   */
  size?: "body1" | "body2";
}

/** How many names the inline summary spells out before "+N more". */
const INLINE_NAME_COUNT = 2;

/**
 * The two experience categories are served by different detail routes, so the
 * link is built from the category the assignment reports rather than guessed.
 */
const experienceHref = ({
  id,
  category,
}: NonNullable<RuleListUsage["experience"]>) =>
  category === "personalization"
    ? `${PERSONALIZATION_ROUTES.Personalization}/${id}`
    : `${EXPERIMENT_ROUTES.Experiment}/${id}`;

/** One name in the popover list; the link and the plain name must match. */
const nameSx = {
  fontSize: 12,
  lineHeight: 1.5,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const SUMMARY_SX = {
  body1: { fontSize: 14, lineHeight: 1.5 },
  body2: { fontSize: 12, lineHeight: 1.5 },
} as const;

/** Grace periods that let the pointer cross the gap into the popover. */
const OPEN_DELAY_MS = 90;
const CLOSE_DELAY_MS = 160;

/**
 * "Used in" answers "which placements", not "how many", so the inline form
 * names the first two. When there are more, a "+N more" link follows them and
 * opens the full list on hover; when the names ARE the list it is plain text,
 * with nothing to open. Either way it stays one line high so row heights never
 * move. The same component renders in the rules list and the rule byline, so
 * the two screens can never disagree.
 */
export const UsedIn = ({
  usages,
  namesColor = "text.secondary",
  size = "body1",
}: UsedInProps) => {
  const theme = useTheme();
  const summarySx = SUMMARY_SX[size];
  const titleId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = null;
  };
  useEffect(() => clear, []);

  const openSoon = (element: HTMLElement) => {
    clear();
    timer.current = setTimeout(() => setAnchorEl(element), OPEN_DELAY_MS);
  };
  const closeSoon = () => {
    clear();
    timer.current = setTimeout(() => setAnchorEl(null), CLOSE_DELAY_MS);
  };

  if (usages === null || usages.length === 0) {
    return (
      <IgTypography color="text.placeholder" noWrap sx={summarySx}>
        {usages === null ? "Unavailable" : "Not used yet"}
      </IgTypography>
    );
  }

  const names = usages
    .slice(0, INLINE_NAME_COUNT)
    .map((usage) => usage.name)
    .join(", ");
  const remaining = usages.length - INLINE_NAME_COUNT;

  if (remaining <= 0) {
    return (
      <IgTypography color={namesColor} noWrap sx={summarySx}>
        {names}
      </IgTypography>
    );
  }

  return (
    <>
      {/* One non-wrapping run: the names give way (ellipsis) before the link
          does, and the single space between them is the names' own trailing
          character, so there is no gap element to drift from the type. */}
      <Stack
        component="span"
        direction="row"
        alignItems="baseline"
        sx={{ ...summarySx, maxWidth: "100%", minWidth: 0 }}
      >
        <IgTypography
          color={namesColor}
          noWrap
          sx={{ ...summarySx, minWidth: 0, whiteSpace: "pre" }}
        >
          {`${names} `}
        </IgTypography>
        <ButtonBase
          data-used-in-more
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchorEl)}
          disableRipple
          onClick={(event) => {
            event.stopPropagation();
            clear();
            setAnchorEl(event.currentTarget);
          }}
          onMouseEnter={(event) => openSoon(event.currentTarget)}
          onMouseLeave={closeSoon}
          onFocus={(event) => {
            clear();
            setAnchorEl(event.currentTarget);
          }}
          sx={{
            ...summarySx,
            color: theme.palette.text.interactive.accent.default,
            flexShrink: 0,
            fontWeight: 600,
            transition: theme.transitions.create("color"),
            whiteSpace: "nowrap",
            "&:hover, &:focus-visible": {
              color: theme.palette.text.interactive.accent.hover,
            },
          }}
        >
          {`+${remaining} more`}
        </ButtonBase>
      </Stack>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          clear();
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableAutoFocus
        disableRestoreFocus
        // The pointer has to be able to reach the list, so the backdrop that
        // Popover normally lays over the page must not eat mouse events.
        slotProps={{
          root: { sx: { pointerEvents: "none" } },
          paper: {
            role: "dialog",
            "aria-labelledby": titleId,
            onMouseEnter: clear,
            onMouseLeave: closeSoon,
            onClick: (event) => event.stopPropagation(),
            // Popover's paper scrolls by default. The card owns its height so
            // the header stays visible and only the list inside it scrolls.
            sx: {
              border: `1px solid ${theme.igBorders.core.default}`,
              borderRadius: "10px",
              boxShadow: theme.igShadows.low,
              display: "flex",
              flexDirection: "column",
              maxHeight: 320,
              mt: 0.75,
              overflow: "hidden",
              pointerEvents: "auto",
              width: 268,
            },
          },
        }}
      >
        <IgTypography
          id={titleId}
          color="text.placeholder"
          sx={{
            borderBottom: `1px solid ${theme.igBorders.core.disabled}`,
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 600,
            lineHeight: 1.5,
            px: 3,
            py: 2,
          }}
        >
          USED IN {usages.length}{" "}
          {usages.length === 1 ? "EXPERIENCE" : "EXPERIENCES"}
        </IgTypography>
        <Stack
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            // minHeight lets this be the flex item that shrinks, so the header
            // keeps its height and only the names scroll.
            minHeight: 0,
            overflowX: "hidden",
            overflowY: "auto",
            py: 1,
          }}
        >
          {usages.map((usage) => (
            <Stack
              component="li"
              key={usage.assignmentId}
              sx={{ px: 3, py: 1.5 }}
            >
              {usage.experience ? (
                <Link
                  component={RouterLink}
                  to={experienceHref(usage.experience)}
                  color="text.primary"
                  underline="hover"
                  sx={{ ...nameSx, display: "block" }}
                >
                  {usage.name}
                </Link>
              ) : (
                <IgTypography color="text.primary" sx={nameSx}>
                  {usage.name}
                </IgTypography>
              )}
              {usage.detail ? (
                <IgTypography
                  color="text.tertiary"
                  sx={{ fontSize: 11, lineHeight: 1.5 }}
                >
                  {usage.detail}
                </IgTypography>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  );
};
